import {
  QuaternionStabilizer,
  YawRecenter,
  computePlacement,
  type BackendCaps,
  type CaptureOptions,
  type DeviceOrientationAngles,
  type PlacementConfig,
  type PoseFrame,
  type TrackingBackend,
} from "@visorxr/core";
import type { EngineContext } from "../engine/engine-context";
import { composeGyroPoseFrame } from "./gyro-pose";
import { isGyroCameraSupported } from "./support";
import { screenOrientationDegrees } from "../platform/screen-orientation";
import { captureCanvasFrame } from "../capture/canvas-capture";
import { acquireRearCamera } from "./rear-camera";
import type { AcquiredCamera } from "./rear-camera";

const GYRO_CAPS: BackendCaps = {
  sixDoF: false,
  hitTest: false,
  worldAnchors: false,
  rawCameraAccess: false,
};

// Level camera looking down -Z, shown for the frame(s) before the first sensor sample arrives.
const IDENTITY_CAMERA = { position: { x: 0, y: 0, z: 0 }, orientation: { x: 0, y: 0, z: 0, w: 1 } };

/**
 * Universal 3DoF backend: getUserMedia rear camera as a self-rendered background, DeviceOrientation
 * for the camera rotation, capture via canvas readback. The only path on iOS; the Android fallback.
 */
export class GyroBackend implements TrackingBackend<EngineContext> {
  readonly id = "gyro" as const;
  readonly caps = GYRO_CAPS;

  private ctx: EngineContext | null = null;
  private camera: AcquiredCamera | null = null;
  private placement: PlacementConfig | null = null;
  private stabilizer = new QuaternionStabilizer();
  private recenter = new YawRecenter();
  private hasOrientationSample = false;
  private readonly angles: DeviceOrientationAngles = { alpha: 0, beta: 0, gamma: 0 };

  // Only a COMPLETE sample counts: a partial event must not establish the recenter baseline.
  private readonly onOrientation = (event: DeviceOrientationEvent): void => {
    if (event.alpha === null || event.beta === null || event.gamma === null) return;
    this.angles.alpha = event.alpha;
    this.angles.beta = event.beta;
    this.angles.gamma = event.gamma;
    this.hasOrientationSample = true;
  };

  isSupported(): Promise<boolean> {
    return Promise.resolve(isGyroCameraSupported(navigator));
  }

  async start(ctx: EngineContext): Promise<void> {
    // Placement is only optional on the type for marker-only descriptors (core/src/types.ts) —
    // this backend renders a floor-elevated glTF, so it has nothing to place without one. Failing
    // here beats a confusing crash inside computePlacement on the first update().
    if (!ctx.descriptor.placement) {
      throw new Error(
        "GyroBackend requires descriptor.placement; a marker-only descriptor has none — only " +
          'list "gyro" in tracking.prefer alongside a placement (and a model to place).',
      );
    }

    this.ctx = ctx;
    this.placement = ctx.descriptor.placement;
    this.stabilizer = new QuaternionStabilizer(ctx.descriptor.tracking.filter);
    this.recenter = new YawRecenter();
    this.hasOrientationSample = false;

    try {
      this.camera = await acquireRearCamera();
      ctx.scene.setBackgroundVideo(this.camera.video);
    } catch (error) {
      // Release a partially-acquired camera so the device light doesn't stay on after a failed start.
      this.camera?.release();
      this.camera = null;
      throw error;
    }

    window.addEventListener("deviceorientation", this.onOrientation);
  }

  update(dt: number): PoseFrame {
    if (!this.placement) throw new Error("GyroBackend.update called before start()");

    // Don't compose (and latch the recenter) until a real sensor sample lands — otherwise the
    // recenter baseline locks to the synthetic zero default and the sign starts at an arbitrary
    // heading on iOS, defeating the gaze-recenter this backend relies on.
    if (!this.hasOrientationSample) {
      return { camera: IDENTITY_CAMERA, object: computePlacement(this.placement), trackingState: "initializing" };
    }

    return composeGyroPoseFrame({
      angles: this.angles,
      screenOrientationDeg: screenOrientationDegrees(),
      placement: this.placement,
      stabilizer: this.stabilizer,
      recenter: this.recenter,
      dt,
    });
  }

  async capture(opts: CaptureOptions): Promise<Blob> {
    if (!this.ctx || !this.camera) throw new Error("GyroBackend.capture called before start()");
    return captureCanvasFrame(this.ctx.scene, this.camera.video, opts);
  }

  stop(): void {
    window.removeEventListener("deviceorientation", this.onOrientation);
    this.ctx?.scene.clearBackground();
    this.camera?.release();
    this.camera = null;
    this.placement = null;
    this.ctx = null;
  }
}
